import MonacoEditor, { type OnMount } from '@monaco-editor/react'
import { DialogActions, DialogContent, DialogTitle } from '@mui/material'
import { isEqual } from 'helpers/global.helper/global.helper'
import React, { memo, ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import uiConfigStore from 'store/uiConfig/uiConfig'
import Button from 'ui/atoms/Button/Button'
import { Dialog } from 'ui/components/Dialog/Dialog'
import { getDocForKey } from 'ui/uiConfig/docs'
import { appendPropsWithDataset } from 'ui/uiConfig/factory'
import { DeepReadonly } from 'utils/types'
import { useSnapshot } from 'valtio'
import { CompareUiConfigVersions } from '../CompareVersions/CompareVersions'
import UiConfigDocsPanel from '../UiConfigDocsPanel/UiConfigDocsPanel'
import AddDatasetForm from './components/AddDatasetForm/AddDatasetForm'
import CopyForm from './components/CopyForm/CopyForm'
import useAutoFixUiConfig from './hooks/useAutoFixUiConfig'
import styles from './UiConfigEditor.module.less'
import {
  type EditablePartOfUiConfig,
  fillValidatedEditableUiConfigDefaults,
  validateEditableUiConfigJson,
} from './UiConfigEditorValidation.helper'

type Props = {
  onCollapsePanel?: () => void
  onExitEditor?: () => void
  onJsonValidityChange?: (isValid: boolean) => void
}

type MonacoPosition = {
  lineNumber: number
  column: number
}

type MonacoWordAtPosition = {
  word: string
  startColumn: number
  endColumn: number
}

type MonacoTextModelLike = {
  getWordAtPosition: (position: MonacoPosition) => MonacoWordAtPosition | null
}

function isMonacoTextModelLike(value: unknown): value is MonacoTextModelLike {
  if (typeof value !== 'object' || value === null) {
    return false
  }

  const modelCandidate = value as { getWordAtPosition?: unknown }
  return typeof modelCandidate.getWordAtPosition === 'function'
}

function extractEditableFields(uiConfig: DeepReadonly<UiConfig>): EditablePartOfUiConfig {
  return {
    display_name: uiConfig.display_name,
    props: uiConfig.props,
    children_ids: uiConfig.children_ids,
    dataset_instructions: uiConfig.dataset_instructions,
    alias: uiConfig.alias,
  } as EditablePartOfUiConfig
}

function UiConfigEditor({ onCollapsePanel, onExitEditor, onJsonValidityChange }: Props): ReactElement {
  const [jsonEditorValue, setJsonEditorValue] = useState('')
  const [datasetFormIsOpen, setAddDatasetFormIsOpen] = useState(false)
  const [copyFormIsOpen, setCopyFormIsOpen] = useState(false)
  const [showCompareDialog, setShowCompareDialog] = useState(false)
  const [showDocsPanel, setShowDocsPanel] = useState(false)
  const [showDiscardDraftDialog, setShowDiscardDraftDialog] = useState(false)
  const hoverComponentNameRef = useRef('')
  const hoverProviderDisposableRef = useRef<{ dispose: () => void } | null>(null)
  const hasInitializedEditorRef = useRef(false)
  const uiConfigSnap = useSnapshot(uiConfigStore)
  const { t } = useTranslation()
  const uid = uiConfigSnap.editingUid
  const uiConfigBeingEdited = uiConfigSnap.editingUiConfig
  const hasEditingUid = typeof uid === 'number'
  const rawUiConfigBeingEdited = hasEditingUid ? uiConfigSnap.uiConfigs[uid] : undefined
  const hasDraft = hasEditingUid ? uiConfigSnap.hasPreviewDraft(uid) : false

  const jsonValidationResult = useMemo(() => {
    return validateEditableUiConfigJson(jsonEditorValue)
  }, [jsonEditorValue])

  const validatedEditableUiConfig = jsonValidationResult.isValid
    ? jsonValidationResult.validatedEditableUiConfig
    : undefined
  const jsonValidationError = jsonValidationResult.isValid ? undefined : jsonValidationResult.validationError
  const isValidJson = jsonValidationResult.isValid

  const saveReadyEditableUiConfig = useMemo(() => {
    if (!validatedEditableUiConfig) {
      return undefined
    }

    return fillValidatedEditableUiConfigDefaults(validatedEditableUiConfig)
  }, [validatedEditableUiConfig])

  const savedConfigNormalized = useMemo(() => {
    if (!rawUiConfigBeingEdited) {
      return undefined
    }

    return fillValidatedEditableUiConfigDefaults(extractEditableFields(rawUiConfigBeingEdited))
  }, [rawUiConfigBeingEdited])

  const editorUiConfigForAutoFix = useMemo(() => {
    if (!uiConfigBeingEdited || !saveReadyEditableUiConfig) {
      return undefined
    }

    return {
      ...uiConfigBeingEdited,
      ...saveReadyEditableUiConfig,
    } as UiConfig
  }, [saveReadyEditableUiConfig, uiConfigBeingEdited])

  const autoFixedInfo = useAutoFixUiConfig({ uiConfig: editorUiConfigForAutoFix })

  useEffect(() => {
    onJsonValidityChange?.(isValidJson)
  }, [isValidJson, onJsonValidityChange])

  useEffect(() => {
    hoverComponentNameRef.current = uiConfigBeingEdited?.component ?? ''
  }, [uiConfigBeingEdited?.component])

  useEffect(() => {
    return () => {
      hoverProviderDisposableRef.current?.dispose()
    }
  }, [])

  useEffect(() => {
    hasInitializedEditorRef.current = false
    const sourceUiConfig = uid != null ? uiConfigStore.getUnparsedUiConfig(uid) : null
    const editableUiConfig = sourceUiConfig ? extractEditableFields(sourceUiConfig) : {}
    const editableUiConfigString = JSON.stringify(editableUiConfig, null, 2)

    setJsonEditorValue((previousValue) => {
      const previousValidationResult = validateEditableUiConfigJson(previousValue)

      if (
        previousValidationResult.isValid &&
        isEqual(JSON.parse(editableUiConfigString), previousValidationResult.validatedEditableUiConfig)
      ) {
        return previousValue
      }

      return editableUiConfigString
    })
  }, [uid])

  useEffect(() => {
    if (uid == null || !rawUiConfigBeingEdited) {
      return
    }

    if (!saveReadyEditableUiConfig) {
      // Clear stale draft when JSON becomes invalid after the editor has initialized,
      // so save controls reflect the actual (unsaveable) editor state.
      if (hasInitializedEditorRef.current) {
        uiConfigStore.clearPreviewDraft(uid)
      }
      return
    }

    hasInitializedEditorRef.current = true

    if (isEqual(savedConfigNormalized, saveReadyEditableUiConfig)) {
      uiConfigStore.clearPreviewDraft(uid)
      return
    }

    uiConfigStore.setPreviewDraft(uid, saveReadyEditableUiConfig)
  }, [saveReadyEditableUiConfig, savedConfigNormalized, uid, rawUiConfigBeingEdited])

  function addNewDataset(datasetInstruction: DatasetInstruction): void {
    const componentName = uiConfigBeingEdited?.component
    if (!componentName || !saveReadyEditableUiConfig) {
      return
    }

    const newJsonEditorValue = {
      ...saveReadyEditableUiConfig,
      props: appendPropsWithDataset(componentName, datasetInstruction, saveReadyEditableUiConfig.props),
      dataset_instructions: [...saveReadyEditableUiConfig.dataset_instructions, datasetInstruction],
    } as UiConfig

    setJsonEditorValue(JSON.stringify(newJsonEditorValue, null, 2))
    setAddDatasetFormIsOpen(false)
  }

  function openCompareDialog(): void {
    setShowCompareDialog(true)
  }
  function resetDraft(): void {
    if (uid == null || !rawUiConfigBeingEdited) {
      return
    }
    uiConfigStore.clearPreviewDraft(uid)
    setJsonEditorValue(JSON.stringify(extractEditableFields(rawUiConfigBeingEdited), null, 2))
    setShowDiscardDraftDialog(false)
  }

  return (
    <div className={`${styles.UiConfigEditor} ${onExitEditor ? styles.UiConfigEditor__mainArea : ''}`}>
      <div className={styles.UiConfigEditor_Header}>
        {onCollapsePanel ? (
          <button
            type="button"
            className={styles.UiConfigEditor_CollapseButton}
            onClick={onCollapsePanel}
            aria-label="Collapse JSON editor panel"
            title="Collapse JSON editor panel"
          >
            ▸
          </button>
        ) : null}

        {onExitEditor ? (
          <button
            type="button"
            className={styles.UiConfigEditor_BackButton}
            onClick={onExitEditor}
            aria-label="Exit JSON editor"
            title="Exit JSON editor"
          >
            ← Preview
          </button>
        ) : null}

        <div className={styles.UiConfigEditor_HeaderActions}>
          <Button
            link
            disabled={!isValidJson}
            disabledTooltip={t('Fix JSON errors before adding a dataset')}
            onClick={() => setAddDatasetFormIsOpen(true)}
          >
            {t(`Add dataset`)}
          </Button>

          <Dialog fullWidth maxWidth="md" open={datasetFormIsOpen} onClose={() => setAddDatasetFormIsOpen(false)}>
            <AddDatasetForm onSubmit={addNewDataset} />
          </Dialog>

          <Button link onClick={() => setCopyFormIsOpen(true)}>
            {t(`Copy`)}
          </Button>

          <Dialog fullWidth maxWidth="md" open={copyFormIsOpen} onClose={() => setCopyFormIsOpen(false)}>
            <CopyForm
              onSubmit={(uiConfig) => {
                setJsonEditorValue(JSON.stringify(uiConfig))
                setCopyFormIsOpen(false)
              }}
            />
          </Dialog>

          <Button link onClick={() => openCompareDialog()}>
            {t(hasDraft ? 'Compare draft with saved version' : 'Compare version')}
          </Button>

          {hasDraft ? (
            <Button link onClick={() => setShowDiscardDraftDialog(true)}>
              {t('Reset draft')}
            </Button>
          ) : null}

          <Button link onClick={() => setShowDocsPanel((v) => !v)}>{`${t('Docs')} ?`}</Button>

          <Dialog
            PaperProps={{
              sx: {
                minHeight: '80vh',
              },
            }}
            fullWidth
            maxWidth="xl"
            open={showCompareDialog}
            onClose={() => setShowCompareDialog(false)}
          >
            <DialogTitle>{t('Compare UI config versions')}</DialogTitle>
            <DialogContent>
              <CompareUiConfigVersions
                config={{
                  ...(uiConfigBeingEdited ?? {}),
                  ...(saveReadyEditableUiConfig ?? {}),
                  component: uiConfigBeingEdited?.component ?? '',
                  props: saveReadyEditableUiConfig?.props ?? uiConfigBeingEdited?.props ?? {},
                  alias: saveReadyEditableUiConfig?.alias ?? uiConfigBeingEdited?.alias ?? {},
                  dataset_instructions:
                    saveReadyEditableUiConfig?.dataset_instructions ?? uiConfigBeingEdited?.dataset_instructions ?? [],
                }}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className={styles.UiConfigEditor_Body}>
        <MonacoEditor
          height="100%"
          language="json"
          value={jsonEditorValue}
          keepCurrentModel
          options={{
            minimap: { enabled: false },
            wordWrap: `on`,
            automaticLayout: true,
            formatOnType: false,
            formatOnPaste: false,
          }}
          onChange={(value) => {
            setJsonEditorValue(value || '')
          }}
          onMount={
            ((editor, monaco) => {
              hoverProviderDisposableRef.current?.dispose()
              hoverProviderDisposableRef.current = monaco.languages.registerHoverProvider('json', {
                provideHover(model: unknown, position: MonacoPosition) {
                  if (!isMonacoTextModelLike(model) || model !== editor.getModel()) {
                    return null
                  }

                  const wordInfo = model.getWordAtPosition(position)
                  if (!wordInfo) {
                    return null
                  }

                  const doc = getDocForKey(hoverComponentNameRef.current, wordInfo.word)
                  if (!doc) {
                    return null
                  }

                  const contents = [
                    { value: `**${wordInfo.word}**${doc.type ? ` \`${doc.type}\`` : ''}` },
                    { value: doc.description },
                    ...(doc.example ? [{ value: `Example: \`${doc.example}\`` }] : []),
                  ]

                  return {
                    range: new monaco.Range(
                      position.lineNumber,
                      wordInfo.startColumn,
                      position.lineNumber,
                      wordInfo.endColumn
                    ),
                    contents,
                  }
                },
              })
            }) satisfies OnMount
          }
        />
        <UiConfigDocsPanel
          componentName={uiConfigBeingEdited?.component}
          onClose={() => setShowDocsPanel(false)}
          isOpen={showDocsPanel}
        />
      </div>

      {(jsonValidationError || autoFixedInfo.hasUpdates) && (
        <div className={styles.UiConfigEditor_Footer}>
          {jsonValidationError ? (
            <div className={styles.UiConfigEditor_ValidationMessage}>{jsonValidationError}</div>
          ) : null}

          {autoFixedInfo.hasUpdates && (
            <Button
              primary
              onClick={() => {
                setJsonEditorValue(JSON.stringify(autoFixedInfo.autoFixedUiConfig, null, 2))
              }}
              tooltip={`${autoFixedInfo.messages.join('\n')}`}
            >
              {t('Apply auto fix') + ` (${autoFixedInfo.messages.length})`}
            </Button>
          )}
        </div>
      )}

      <Dialog open={showDiscardDraftDialog} onClose={() => setShowDiscardDraftDialog(false)}>
        <DialogTitle fontWeight="bold">{t('Discard unsaved draft?')}</DialogTitle>
        <DialogContent>
          <div>{t('This will revert the editor to the last saved version and remove the local draft.')}</div>
        </DialogContent>
        <DialogActions>
          <Button secondary onClick={() => setShowDiscardDraftDialog(false)}>
            {t('Cancel')}
          </Button>
          <Button danger onClick={resetDraft}>
            {t('Discard draft')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default memo(UiConfigEditor)
